/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */

import { html, LitElement } from 'lit-element';
import '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { ControlStateMixin } from '../../utils/control-state-mixin.js';
import { extendInteractiveArea } from '../../utils/interactive-area.js';
import switchItemStyles from './sh-switch-item.lit.scss.js';

/**
 * @prop {String} label - Defines the label of the item.
 * @prop {String} icon - Defines the icon shown inside the item.
 * @prop {Boolean} active - Default false. Defines whether the item is active or not. Will be set to true once the item is clicked, and to false if a sibling item is clicked.
 * @prop {Boolean} disabled - Default false. If set to `true`, the switch item will not allow any interaction.
 * @prop {Boolean} condensed - Enables condensed appearance.
 */
export class SHSwitchItem extends ControlStateMixin(LitElement) {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      active: { type: Boolean, reflect: true },
      condensed: { type: Boolean, reflect: true },
      icon: { type: String, reflect: true },
    };
  }
  static get styles() {
    return [switchItemStyles, extendInteractiveArea()];
  }

  render() {
    return html`
      ${this.label && !this.icon ? html` <span class="label"> ${this.label} </span>` : ''}
      ${this.icon && !this.label
        ? html` <sh-icon icon="${this.icon}" class="switch-icon"></sh-icon>`
        : ''}
    `;
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('active') && this.active) {
      this.handleActiveChange();
    }
    const listOfProperties = ['label', 'active', 'condensed', 'icon'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }
  firstUpdated() {
    super.firstUpdated();
    this.addEventListener('click', () => this.handleClick());
    this.addEventListener('focus', () => this.handleFocus());
    this.addEventListener('blur', () => this.handleBlur());
  }
  handleClick() {
    this.active = true;
  }
  handleFocus() {
    this.addEventListener('keyup', this.handleKeyup);
  }
  handleBlur() {
    this.removeEventListener('keyup', this.handleKeyup);
    this.removeAttribute('blue-outline');
  }
  handleKeyup(e) {
    const tabKey = 9;
    const spaceKey = 32;
    const enterKey = 13;
    if (e.keyCode === tabKey) {
      this.setAttribute('blue-outline', '');
    }
    if (e.keyCode === spaceKey || e.keyCode === enterKey) {
      this.click();
    }
  }
  handleActiveChange() {
    const siblingItems = this.parentNode.children;
    const numberOfSiblings = siblingItems.length;
    for (let i = 0; i < numberOfSiblings; i++) {
      const sibling = siblingItems[i];
      if (sibling.hasAttribute('active') && sibling !== this) {
        sibling.active = false;
      }
    }
  }
}

customElements.define('sh-switch-item', SHSwitchItem);
